import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Wrench, Plus, Download, Search, Filter, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  Draft:      { bg: 'bg-slate-100 text-slate-700 border-slate-200',       label: 'Draft' },
  Received:   { bg: 'bg-blue-100 text-blue-800 border-blue-200',          label: 'Received' },
  Diagnosing: { bg: 'bg-amber-100 text-amber-800 border-amber-200',       label: 'Diagnosing' },
  Repairing:  { bg: 'bg-orange-100 text-orange-800 border-orange-200',    label: 'Repairing' },
  Testing:    { bg: 'bg-purple-100 text-purple-800 border-purple-200',    label: 'Testing' },
  Returned:   { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Returned' },
  Closed:     { bg: 'bg-slate-900 text-white border-slate-900',           label: 'Closed' },
  Canceled:   { bg: 'bg-red-100 text-red-800 border-red-200',             label: 'Canceled' },
};

const RepairOrdersPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [ros, setRos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      const qs = new URLSearchParams(params).toString();
      const [r, c] = await Promise.all([
        api().get(`/repair-orders${qs ? `?${qs}` : ''}`),
        api().get('/customers'),
      ]);
      setRos(r.data || []);
      setCustomers(c.data || []);
    } catch { toast.error('Failed to load repair orders'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, customerFilter]);

  const bulkCsv = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      const qs = new URLSearchParams(params).toString();
      const res = await api().get(`/repair-orders/export.csv${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `repair-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bulk CRM export downloaded');
    } catch { toast.error('Bulk export failed'); }
  };

  const filtered = ros.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.ro_number || '').toLowerCase().includes(q)
      || (r.customer_name_snapshot || '').toLowerCase().includes(q)
      || (r.product_name || '').toLowerCase().includes(q)
      || (r.product_serial || '').toLowerCase().includes(q);
  });

  const stats = ros.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="repair-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Wrench className="w-7 h-7 text-slate-700" /> Lyphline Repair Orders</h1>
          <p className="text-sm text-slate-500 mt-1">Customer intake → diagnose → repair → return. Full lifecycle with warranty tracking and parts/labor costing.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={bulkCsv} className="rounded-sm" data-testid="ro-bulk-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Bulk CRM Export
          </Button>
          <Button onClick={() => navigate('/orders/repairs/new')} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ro-create-btn">
            <Plus className="w-4 h-4 mr-1" /> New Lyphline Repair Order
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
        {Object.keys(STATUS_META).map(k => (
          <Card key={k} className="industrial-card">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold" data-testid={`ro-stat-${k.toLowerCase()}`}>{stats[k] || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{STATUS_META[k].label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-lg">All Lyphline Repair Orders ({filtered.length})</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-56">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search RO#, customer, serial…" className="pl-8 rounded-sm" data-testid="ro-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 rounded-sm" data-testid="ro-status-filter"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.keys(STATUS_META).map(k => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="w-48 rounded-sm" data-testid="ro-customer-filter"><SelectValue placeholder="Customer…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Wrench className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No Lyphline Repair Orders yet</p>
              <p className="text-xs mt-1">Click "New Lyphline Repair Order" to intake a customer unit (you'll need at least 1 <Link to="/customers" className="underline hover:text-slate-900">Customer</Link> on file).</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>RO #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Serial</TableHead>
                  <TableHead>Intake</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Warranty</TableHead>
                  <TableHead className="text-right">Total $</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(r => {
                  const m = STATUS_META[r.status] || STATUS_META.Draft;
                  return (
                    <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/repairs/${r.id}`)} data-testid={`ro-row-${r.ro_number}`}>
                      <TableCell className="font-semibold mono">{r.ro_number}</TableCell>
                      <TableCell>{r.customer_name_snapshot}</TableCell>
                      <TableCell className="text-xs">{r.product_name}{r.product_model && <span className="text-slate-400"> · {r.product_model}</span>}</TableCell>
                      <TableCell className="text-xs mono">{r.product_serial || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-xs">{r.intake_date}</TableCell>
                      <TableCell><Badge className={`${m.bg} border rounded-sm text-xs`}>{m.label}</Badge></TableCell>
                      <TableCell className="text-center">{r.warranty_covered ? <ShieldCheck className="w-4 h-4 text-blue-600 inline" /> : <span className="text-slate-300 text-xs">—</span>}</TableCell>
                      <TableCell className="text-right mono text-xs">${Number(r.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RepairOrdersPage;
